import React, { useEffect, useRef, useState } from "react";
import planBackdrop from "../../../assets/svg/plan/plan-background-image.svg";
import { PRODUCT_NAME, TESTIMONIALS } from "../data/subscriptionPlans";
import type { PlanFeature } from "../types/billing.types";

/** How long one quote holds before the next takes its place. */
const ROTATE_MS = 6000;

interface PlanShowcaseProps {
  /** What the product includes, as chips. The price list's own feature copy —
   *  see `PRO_FEATURES` — passed in rather than read here so the panel draws
   *  whatever the plan beside it is advertising. */
  features: PlanFeature[];
  /** The line under the product name. */
  tagline?: string;
}

/**
 * What is being sold, as a picture: the product, what it covers, and a
 * practitioner saying it is worth having.
 *
 * It stands beside the duration list rather than above it, because the two are
 * answering different questions — this one is "what is Naurist Pro", the list
 * beside it is "for how long". On a phone the question still comes first, so it
 * stacks on top.
 *
 * ## The quotes move on their own
 *
 * Four of them, one at a time, every six seconds — long enough to read a
 * sentence of that length twice. Two things stop it, and both are the same
 * rule: a panel that moves under somebody who is reading it is worse than one
 * that never moved at all.
 *
 *   - The pointer resting on the panel pauses it, and leaving resumes it.
 *   - `prefers-reduced-motion` turns it off before it ever starts.
 *
 * There is no way to pick a quote by hand. The dots that did that are gone,
 * and with them the only reason this had a stopped-for-good state: nothing
 * can now say "this is the one I am reading" except resting the pointer on
 * it, which is what the pause is for.
 *
 * Each quote arrives from the right and settles left, so the motion says which
 * way it is going through the list rather than only that something changed. It
 * sits in a live region that outlives it — see the note on the figure — so the
 * change is announced rather than silently swapping text under a screen
 * reader.
 *
 * The backdrop is one supplied artwork drawn as a background rather than an
 * `<img>`: it is a rendered gradient that the text sits *on*, and the panel has
 * to keep its own shape at every width while the artwork is cropped to it.
 */
const PlanShowcase: React.FC<PlanShowcaseProps> = ({
  features,
  tagline = "Your complete practice, in one place!",
}) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;

  // Read once at mount rather than subscribed to: somebody who changes this
  // setting mid-checkout is reloading the page anyway, and a listener here
  // would outlive the panel on a screen that is about to navigate.
  const reduced = useRef(
    typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (paused || reduced.current || count < 2) return;

    const timer = setInterval(
      () => setIndex((current) => (current + 1) % count),
      ROTATE_MS
    );
    return () => clearInterval(timer);
  }, [paused, count]);

  const showing = TESTIMONIALS[index] ?? TESTIMONIALS[0];

  return (
    <section
      aria-label={`${PRODUCT_NAME} overview`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      // The design's aspect on the wide layout, and a plain minimum height on
      // a phone, where the chips wrap to three or four rows.
      //
      // `lg:min-h-fit` is what keeps the aspect a *floor* rather than a cage.
      // An aspect ratio fixes the height to the width, so on a narrow two-up
      // — a laptop, or a window with the devtools open beside it — the column
      // gets short enough that the quote is cut off by the `overflow-hidden`
      // that rounds these corners. `min-height: fit-content` lets the content
      // win whenever it needs more room than the ratio gives, and changes
      // nothing at the widths where the ratio was already the bigger of the
      // two. It replaced `lg:min-h-0`, which said the opposite: shrink below
      // the content and clip it.
      className="relative isolate flex min-h-[380px] flex-col overflow-hidden card-surface p-6 text-white sm:p-7 lg:aspect-[613/444] lg:min-h-fit 3xl:p-8"
      style={{
        backgroundImage: `url(${planBackdrop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        // The deep navy the artwork opens on, so the panel is never white
        // behind its own white text while the image is still arriving.
        backgroundColor: "#070E22",
      }}
    >
      <h2 className="text-[28px] sm:text-[32px] 3xl:text-[38px] font-bold leading-[1.1] text-[#2FE3F2]">
        {PRODUCT_NAME}
      </h2>
      <p className="mt-1 text-[16px] sm:text-[18px] 3xl:text-[20px] font-medium">
        {tagline}
      </p>

      {/* What is included, as chips rather than a ticked list: the list beside
          this panel is where the reading happens, and this is the glance. */}
      <ul className="mt-5 flex flex-wrap gap-2 3xl:mt-6">
        {features.map((feature) => (
          <li
            key={feature.name}
            className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] 3xl:text-[13px] font-medium text-white backdrop-blur-[2px]"
          >
            {feature.name}
          </li>
        ))}
      </ul>

      {/* Pinned to the foot of the panel and then lifted clear of it: the
          quote sits over the brightest part of the artwork, and the 32px keeps
          it off the bottom edge rather than reading as something that has
          slipped down there. `mt-auto` is what puts it at the foot in the first
          place, so the margin below is what the lift has to be made of. */}
      <figure className="mt-auto mb-8 pt-10">
        {/* The live region is the wrapper, not the quote itself.

            That is load-bearing: the quote inside is keyed by which testimonial
            it is, so React replaces the element outright and the entry
            animation runs again on every change. A region that is itself torn
            down and rebuilt is a region a screen reader may never announce the
            change of — the thing being watched has to outlive the thing being
            swapped.

            Polite rather than assertive: it replaces itself on a timer, and
            interrupting whatever is being read elsewhere on the page for a
            marketing quote would be rude. */}
        <div aria-live="polite" aria-atomic="true">
          <blockquote
            key={index}
            // In from the right, settling left — the direction the list is
            // moving. `motion-safe` only: the rotation is already off for
            // anyone who asked for less motion, and this is the other half of
            // that promise, for the quote they picked by hand.
            className="text-[13px] sm:text-[14px] 3xl:text-[15px] font-medium leading-[1.6] motion-safe:animate-quote-in"
          >
            &ldquo;{showing.quote}&rdquo;
          </blockquote>
        </div>

        <figcaption className="mt-4 flex items-center gap-3">
          {/* Keyed and animated with the quote above, so the person and what
              they said arrive together rather than the name snapping into
              place under a line that is still sliding. */}
          <span
            key={index}
            className="flex min-w-0 flex-1 items-center gap-3 motion-safe:animate-quote-in"
          >
            {/* The practitioner's initial. No photograph: these are quotes
                given for the panel, and inventing a face to go with a name is
                the one thing a testimonial must not do. */}
            <span
              aria-hidden="true"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#9B7BEA] text-[14px] font-semibold text-white 3xl:h-9 3xl:w-9"
            >
              {showing.name.charAt(0)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] 3xl:text-[14px] font-semibold">
                {showing.name}
              </span>
              <span className="block truncate text-[11px] 3xl:text-[12px] text-white/70">
                {showing.location}
              </span>
            </span>
          </span>
        </figcaption>
      </figure>
    </section>
  );
};

export default PlanShowcase;
